/**
 * Top header — a breadcrumb of the current location, a centred search
 * placeholder, and the notifications bell.
 *
 * The per-viewer controls — theme, settings, notifications — sit together at the
 * right. They are **here and nowhere else**: a second copy in the navbar would
 * be a control that can disagree with this one, and the header is always on
 * screen, so there is no state where they are out of reach.
 *
 * **It carries no navigation toggle.** That one has to stay with the thing it
 * collapses, and the rail is never fully hidden, so it is always reachable.
 *
 * Tenant-administration surfaces (User management, Audit log) live in the left
 * navigation's "Administration" section, not here.
 */

import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Group,
  TextInput,
  Tooltip,
  rem,
} from "@mantine/core";
import type { MantineColor } from "@mantine/core";
import { IconSearch, IconSettings } from "@tabler/icons-react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuthConfig } from "@/features/auth/useAuthConfig";
import { crumbsFor } from "./breadcrumbs";
import { NotificationsMenu } from "./NotificationsMenu";

/** Badge colour per deployment environment — a visual "where am I" cue. */
const ENV_COLOR: Record<string, MantineColor> = {
  production: "red",
  staging: "yellow",
  dev: "blue",
  local: "gray",
};

export function HeaderPanel() {
  const { env, env_title: envTitle } = useAuthConfig();
  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap" align="center">
      <Group gap="md" wrap="nowrap" align="center" style={{ minWidth: 0, flex: 1 }}>
        <Breadcrumb />
      </Group>

      <TextInput
        placeholder="Search…"
        leftSection={<IconSearch size={16} />}
        size="sm"
        radius="md"
        style={{ width: rem(320) }}
        aria-label="Global search (placeholder)"
        disabled
      />

      <Group gap="xs" justify="flex-end" wrap="nowrap" style={{ flex: 1 }}>
        {envTitle ? (
          <Badge
            color={ENV_COLOR[env] ?? "gray"}
            variant="filled"
            radius="sm"
            tt="none"
            styles={{
              root: { height: rem(32), paddingInline: rem(16) },
              label: {
                color: "#fff",
                fontSize: rem(15),
                fontWeight: 700,
                letterSpacing: "0.03em",
              },
            }}
            aria-label={`Environment: ${envTitle}`}
          >
            {envTitle}
          </Badge>
        ) : null}
        <Tooltip label="Settings">
          <ActionIcon
            component={RouterLink}
            to="/settings"
            variant="subtle"
            color="gray"
            size="lg"
            aria-label="Settings"
          >
            <IconSettings size={18} />
          </ActionIcon>
        </Tooltip>
        <NotificationsMenu />
      </Group>
    </Group>
  );
}

function Breadcrumb() {
  const { pathname } = useLocation();
  const items = crumbsFor(pathname);

  return (
    <Breadcrumbs separator="›" separatorMargin="xs">
      {items.map((it, i) =>
        i === items.length - 1 ? (
          <Box key={`${it.to}-${i}`} fw={500}>
            {it.label}
          </Box>
        ) : (
          <Anchor key={`${it.to}-${i}`} component={RouterLink} to={it.to} c="dimmed" size="sm">
            {it.label}
          </Anchor>
        ),
      )}
    </Breadcrumbs>
  );
}
